import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, Plus, Search, ToggleLeft, ToggleRight, Key, Calendar, 
  AlertTriangle, FileArchive, Pencil, Download, Upload, Eye,
  Building, CreditCard, Car, User, FileText, Trash2, FileSignature,
  ClipboardCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';
import { ViewDriverDialog } from '@/components/drivers/ViewDriverDialog';
import { ExpiryDialog } from '@/components/drivers/ExpiryDialog';
import { DeleteDriverDialog } from '@/components/drivers/DeleteDriverDialog';
import { DocsDialog } from '@/components/drivers/DocsDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  const [docsDriver, setDocsDriver] = useState(null);
  const [driverDocs, setDriverDocs] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  const [newDriver, setNewDriver] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  
  // Complete edit data structure matching user's requested format
  const [editData, setEditData] = useState({
    // Owner Details
    full_name: '',
    email: '',
    password: '',
    id_number: '',
    address: '',
    contact_number: '',
    company_trading_as: '',
    // Bank Details
    bank_name: '',
    account_number: '',
    branch_code: '',
    account_type: '',
    // Driver Details
    driver_name: '',
    driver_id_number: '',
    driver_contact_number: '',
    driver_license: '',
    license_disc: '',
    pdp: '',
    criminal_checks: '',
    // Vehicle Details
    vehicle_registration: '',
    vehicle_type: '',
    vehicle_comments: '',
    // Site allocation
    allocated_site_id: '',
    allocated_site_name: '',
    // Finance & Comments
    finance_comments: '',
    general_comments: ''
  });
  
  // Sites list for site picker in edit dialog
  const [sites, setSites] = useState([]);
  
  const [expiryData, setExpiryData] = useState({
    license_expiry: '',
    pdp_expiry: '',
    vehicle_docs_expiry: ''
  });

  useEffect(() => {
    fetchDrivers();
    fetchExpiringDocs();
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const res = await axios.get(`${API_URL}/sites`);
      setSites(res.data || []);
    } catch { /* silent */ }
  };

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API_URL}/drivers`);
      setDrivers(response.data);
    } catch (error) {
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiringDocs = async () => {
    try {
      const response = await axios.get(`${API_URL}/drivers/expiring-documents?days=30`);
      setExpiringDocs(response.data.drivers || []);
    } catch (error) {
      console.error('Failed to fetch expiring docs');
    }
  };

  const openDocsDialog = async (driver) => {
    setDocsDriver(driver);
    setShowDocsDialog(true);
    // Fetch document status for this driver record directly
    try {
      const res = await axios.get(`${API_URL}/drivers/${driver.id}/documents`);
      setDriverDocs(res.data);
    } catch (e) {
      setDriverDocs({});
    }
  };

  const handleDocUpload = async (driverId, docType, file) => {
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('doc_type', docType);
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/drivers/${driverId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded!');
      // Refresh docs
      const res = await axios.get(`${API_URL}/drivers/${driverId}/documents`);
      setDriverDocs(res.data);
      fetchDrivers();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    
    try {
      await axios.post(`${API_URL}/drivers`, newDriver);
      toast.success('Driver created successfully');
      setShowCreateDialog(false);
      setNewDriver({ email: '', password: '', full_name: '' });
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create driver'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditDriver = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    
    try {
      // Only send fields that have values
      const updatePayload = Object.fromEntries(
        Object.entries(editData).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      
      await axios.put(`${API_URL}/drivers/${selectedDriver.owner_user_id}`, updatePayload);
      toast.success('Driver updated successfully');
      setShowEditDialog(false);
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update driver'));
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleStatus = async (driverId) => {
    try {
      const response = await axios.put(`${API_URL}/drivers/${driverId}/toggle-status`);
      toast.success(response.data.message);
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to update driver status');
    }
  };

  const handleResetPassword = async (driverId) => {
    try {
      const response = await axios.post(`${API_URL}/drivers/${driverId}/reset-password`);
      toast.success(`Password reset. Temporary password: ${response.data.temporary_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleUpdateExpiry = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/drivers/${selectedDriver.owner_user_id}/document-expiry`, expiryData);
      toast.success('Document expiry dates updated');
      setShowExpiryDialog(false);
      fetchDrivers();
      fetchExpiringDocs();
    } catch (error) {
      toast.error('Failed to update expiry dates');
    }
  };

  const handleExportDriverDocs = async (driverId, driverName) => {
    try {
      const response = await axios.get(`${API_URL}/export/driver-documents/${driverId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${driverName.replace(/\s+/g, '_')}_documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Documents exported');
    } catch (error) {
      toast.error('Failed to export documents');
    }
  };

  const handleExportAllDriversCSV = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/drivers-full`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_drivers_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Drivers exported to CSV');
    } catch (error) {
      toast.error('Failed to export drivers');
    }
  };

  const handleImportDrivers = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API_URL}/import/drivers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Import complete: ${response.data.imported} new, ${response.data.updated} updated`);
      setShowImportDialog(false);
      setImportFile(null);
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import drivers'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleDeleteDriver = async () => {
    if (!driverToDelete) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API_URL}/drivers/${driverToDelete.owner_user_id}`);
      toast.success(`Driver "${driverToDelete.driver_name}" deleted successfully`);
      setShowDeleteDialog(false);
      setDriverToDelete(null);
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete driver'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (driver) => {
    setDriverToDelete(driver);
    setShowDeleteDialog(true);
  };

  const handleGenerateContract = async (driver) => {
    try {
      const formData = new FormData();
      formData.append('driver_id', driver.id);
      formData.append('contract_type', 'Owner-Driver Courier Service Agreement');
      const response = await axios.post(`${API_URL}/contracts`, formData);
      const contractId = response.data.id;
      toast.success(`Contract generated for ${driver.driver_name}`);
      // Open contract in new tab
      window.open(`/admin/contracts/${contractId}`, '_blank');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate contract'));
    }
  };

  const handleGenerateOnboarding = async (driver) => {
    try {
      const response = await axios.post(`${API_URL}/drivers/${driver.id}/onboarding-docs`);
      toast.success(response.data.message || `Onboarding pack generated for ${driver.driver_name}`);
      // Download single combined Excel file
      const downloadUrl = response.data.download_url;
      if (downloadUrl) {
        const token = localStorage.getItem('token');
        const docResp = await axios.get(`${API_URL}${downloadUrl}`, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` }
        });
        const blobUrl = window.URL.createObjectURL(new Blob([docResp.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }));
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `Onboarding_Pack_${driver.driver_name.replace(/\s+/g, '_')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      }
      fetchDrivers();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate onboarding pack'));
    }
  };

  const openEditDialog = (driver) => {
    setSelectedDriver(driver);
    setEditData({
      full_name: driver.owner_name || '',
      email: driver.owner_username || '',
      password: '',
      id_number: driver.owner_id_number || '',
      address: driver.owner_address || '',
      contact_number: driver.owner_contact || '',
      company_trading_as: driver.company_trading_as || '',
      bank_name: driver.bank_name || '',
      account_number: driver.account_number || '',
      branch_code: driver.branch_code || '',
      account_type: driver.account_type || '',
      driver_name: driver.driver_name || '',
      driver_id_number: driver.driver_id_number || '',
      driver_contact_number: driver.driver_contact || '',
      driver_license: driver.doc_drivers_license || '',
      license_disc: driver.doc_license_disc || '',
      pdp: driver.doc_page_28 || '',
      criminal_checks: driver.doc_criminal_checks || '',
      vehicle_registration: driver.registration_number || '',
      vehicle_type: driver.vehicle_type || '',
      vehicle_comments: driver.vehicle_comments || '',
      allocated_site_id: driver.allocated_site_id || '',
      allocated_site_name: driver.allocated_site_name || '',
      finance_comments: driver.finance_comments || '',
      general_comments: driver.general_comments || ''
    });
    setShowEditDialog(true);
  };

  const openViewDialog = async (driver) => {
    // Use the driver record directly since the flat list already has all info
    setSelectedDriver(driver);
    setShowViewDialog(true);
  };

  const openExpiryDialog = (driver) => {
    setSelectedDriver(driver);
    setExpiryData({
      license_expiry: driver.license_expiry || '',
      pdp_expiry: driver.pdp_expiry || '',
      vehicle_docs_expiry: driver.vehicle_docs_expiry || ''
    });
    setShowExpiryDialog(true);
  };

  const filteredDrivers = drivers.filter(driver => {
    // First filter by search
    const q = search.toLowerCase();
    const matchesSearch = driver.driver_name?.toLowerCase().includes(q) ||
      driver.owner_name?.toLowerCase().includes(q) ||
      driver.driver_id_number?.toLowerCase().includes(q) ||
      driver.driver_username?.toLowerCase().includes(q) ||
      driver.owner_username?.toLowerCase().includes(q);
    
    if (!matchesSearch) return false;
    
    // Then filter by active tab
    if (activeTab === 'active') return driver.is_active === true;
    if (activeTab === 'inactive') return driver.is_active === false;
    return true; // 'all' tab shows everything
  });

  // Count for tabs
  const activeCount = drivers.filter(d => d.is_active === true).length;
  const inactiveCount = drivers.filter(d => d.is_active === false).length;

  const getExpiryBadge = (driver) => {
    const expiring = expiringDocs.find(e => e.driver_id === driver.id);
    if (expiring) {
      const urgent = expiring.alerts.some(a => a.days_left <= 7);
      return (
        <Badge className={urgent ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
          <AlertTriangle className="h-3 w-3 mr-1" />
          {expiring.alerts.length} expiring
        </Badge>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-drivers">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Drivers</h1>
            <p className="text-muted-foreground mt-1">Manage driver accounts and documents</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Export CSV Button */}
            <Button variant="outline" onClick={handleExportAllDriversCSV} data-testid="export-csv-button">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            
            {/* Import Button */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-button">
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Import Drivers</DialogTitle>
                  <DialogDescription>
                    Upload a CSV or Excel file with driver data. Existing drivers will be updated.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleImportDrivers}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>CSV or Excel File</Label>
                      <Input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                        data-testid="import-file-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Accepts CSV or Excel (.xlsx, .xls) files with columns: OWNER USER NAME, OWNER NAME, OWNER ID NUMBER, etc.
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={importLoading || !importFile} data-testid="submit-import">
                      {importLoading ? 'Importing...' : 'Import Drivers'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* Create Driver Button */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-driver-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Driver
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Driver</DialogTitle>
                  <DialogDescription>
                    Create a new driver account with a simple username and password.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateDriver}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        value={newDriver.full_name}
                        onChange={(e) => setNewDriver({ ...newDriver, full_name: e.target.value })}
                        required
                        data-testid="new-driver-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        type="text"
                        value={newDriver.email}
                        onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })}
                        required
                        placeholder="e.g., john, driver1, any name"
                        data-testid="new-driver-email-input"
                      />
                      <p className="text-xs text-gray-500">Can be any name - no email required</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newDriver.password}
                        onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })}
                        required
                        data-testid="new-driver-password-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createLoading} data-testid="submit-create-driver">
                      {createLoading ? 'Creating...' : 'Create Driver'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Expiring Documents Alert */}
        {expiringDocs.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">Document Expiry Alert</p>
                  <p className="text-sm text-amber-700">
                    {expiringDocs.length} driver{expiringDocs.length !== 1 ? 's have' : ' has'} documents expiring within 30 days
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active" className="gap-2">
              <ToggleRight className="h-4 w-4 text-green-600" />
              Active ({activeCount})
            </TabsTrigger>
            <TabsTrigger value="inactive" data-testid="tab-inactive" className="gap-2">
              <ToggleLeft className="h-4 w-4 text-slate-400" />
              Inactive ({inactiveCount})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              All ({drivers.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search drivers by name, email or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-drivers-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Drivers Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              {activeTab === 'active' ? 'Active Drivers' : activeTab === 'inactive' ? 'Inactive Drivers' : 'All Drivers'}
            </CardTitle>
            <CardDescription>
              {filteredDrivers.length} driver{filteredDrivers.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredDrivers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium mb-2">No drivers found</h3>
                <p className="text-muted-foreground">Create a new driver to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver Name</TableHead>
                      <TableHead>Owner Driver</TableHead>
                      <TableHead>Driver ID</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDrivers.map((driver) => (
                      <TableRow key={driver.id} data-testid={`driver-row-${driver.id}`}>
                        <TableCell className="font-medium">
                          <div>
                            <span>{driver.driver_name}</span>
                            {driver.is_self_owned && (
                              <Badge variant="outline" className="ml-2 text-[10px] py-0">Owner</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{driver.owner_name}</TableCell>
                        <TableCell className="text-sm">{driver.driver_id_number || '-'}</TableCell>
                        <TableCell>{driver.driver_contact || '-'}</TableCell>
                        <TableCell className="text-sm">{driver.registration_number || '-'}</TableCell>
                        <TableCell>
                          <Badge className={driver.is_active ? 'status-active' : 'status-inactive'}>
                            {driver.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getExpiryBadge(driver)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDocsDialog(driver)}
                              title="Manage Documents"
                              data-testid={`docs-${driver.id}`}
                              className="text-emerald-600 hover:text-emerald-700"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openViewDialog(driver)}
                              title="View Details"
                              data-testid={`view-${driver.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(driver)}
                              title="Edit Driver"
                              data-testid={`edit-${driver.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openExpiryDialog(driver)}
                              title="Manage Document Expiry"
                              data-testid={`expiry-${driver.id}`}
                            >
                              <Calendar className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleGenerateOnboarding(driver)}
                              title="Generate Onboarding Documents (DPD)"
                              data-testid={`onboarding-${driver.id}`}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <ClipboardCheck className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleGenerateContract(driver)}
                              title="Generate Contract"
                              data-testid={`contract-${driver.id}`}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <FileSignature className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportDriverDocs(driver.id, driver.driver_name)}
                              title="Export All Documents"
                              data-testid={`export-docs-${driver.id}`}
                            >
                              <FileArchive className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(driver.owner_user_id)}
                              title={driver.is_active ? 'Deactivate' : 'Activate'}
                              data-testid={`toggle-status-${driver.id}`}
                            >
                              {driver.is_active ? (
                                <ToggleRight className="h-4 w-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResetPassword(driver.owner_user_id)}
                              title="Reset Password"
                              data-testid={`reset-password-${driver.id}`}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(driver)}
                              title="Delete Driver"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${driver.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Driver Dialog */}
        <ViewDriverDialog
          open={showViewDialog}
          onOpenChange={setShowViewDialog}
          selectedDriver={selectedDriver}
          onEdit={openEditDialog}
          onRefresh={fetchDrivers}
        />

        {/* Edit Driver Dialog - Full Form with Tabs */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Edit Driver</DialogTitle>
              <DialogDescription>
                Update all information for {selectedDriver?.driver_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditDriver}>
              <Tabs defaultValue="owner" className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-4">
                  <TabsTrigger value="owner" className="text-xs">Owner</TabsTrigger>
                  <TabsTrigger value="bank" className="text-xs">Bank</TabsTrigger>
                  <TabsTrigger value="driver" className="text-xs">Driver</TabsTrigger>
                  <TabsTrigger value="vehicle" className="text-xs">Vehicle</TabsTrigger>
                  <TabsTrigger value="comments" className="text-xs">Comments</TabsTrigger>
                </TabsList>
                
                <ScrollArea className="h-[400px] pr-4">
                  {/* Owner Tab */}
                  <TabsContent value="owner" className="space-y-4 mt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_full_name">OWNER NAME *</Label>
                        <Input
                          id="edit_full_name"
                          value={editData.full_name}
                          onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                          data-testid="edit-owner-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_email">OWNER USERNAME *</Label>
                        <Input
                          id="edit_email"
                          type="text"
                          value={editData.email}
                          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                          data-testid="edit-owner-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_password">OWNER PASSWORD (leave blank to keep)</Label>
                        <Input
                          id="edit_password"
                          type="password"
                          value={editData.password}
                          onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                          placeholder="Enter new password"
                          data-testid="edit-owner-password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_id_number">OWNER ID NUMBER</Label>
                        <Input
                          id="edit_id_number"
                          value={editData.id_number}
                          onChange={(e) => setEditData({ ...editData, id_number: e.target.value })}
                          data-testid="edit-owner-id"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="edit_address">OWNER ADDRESS</Label>
                        <Input
                          id="edit_address"
                          value={editData.address}
                          onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                          data-testid="edit-owner-address"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_contact_number">OWNER CELL</Label>
                        <Input
                          id="edit_contact_number"
                          value={editData.contact_number}
                          onChange={(e) => setEditData({ ...editData, contact_number: e.target.value })}
                          data-testid="edit-owner-cell"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_company">COMPANY TRADING AS</Label>
                        <Input
                          id="edit_company"
                          value={editData.company_trading_as}
                          onChange={(e) => setEditData({ ...editData, company_trading_as: e.target.value })}
                          data-testid="edit-company"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Bank Tab */}
                  <TabsContent value="bank" className="space-y-4 mt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_bank_name">BANK NAME</Label>
                        <Input
                          id="edit_bank_name"
                          value={editData.bank_name}
                          onChange={(e) => setEditData({ ...editData, bank_name: e.target.value })}
                          placeholder="e.g., FNB, ABSA, Standard Bank"
                          data-testid="edit-bank-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_account_number">ACCOUNT NUMBER</Label>
                        <Input
                          id="edit_account_number"
                          value={editData.account_number}
                          onChange={(e) => setEditData({ ...editData, account_number: e.target.value })}
                          data-testid="edit-account-number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_branch_code">BRANCH CODE</Label>
                        <Input
                          id="edit_branch_code"
                          value={editData.branch_code}
                          onChange={(e) => setEditData({ ...editData, branch_code: e.target.value })}
                          data-testid="edit-branch-code"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_account_type">ACCOUNT TYPE</Label>
                        <Input
                          id="edit_account_type"
                          value={editData.account_type}
                          onChange={(e) => setEditData({ ...editData, account_type: e.target.value })}
                          placeholder="e.g., Cheque, Savings"
                          data-testid="edit-account-type"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Driver Tab */}
                  <TabsContent value="driver" className="space-y-4 mt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_driver_name">DRIVER NAME</Label>
                        <Input
                          id="edit_driver_name"
                          value={editData.driver_name}
                          onChange={(e) => setEditData({ ...editData, driver_name: e.target.value })}
                          data-testid="edit-driver-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_driver_id_number">DRIVER ID NUMBER</Label>
                        <Input
                          id="edit_driver_id_number"
                          value={editData.driver_id_number}
                          onChange={(e) => setEditData({ ...editData, driver_id_number: e.target.value })}
                          data-testid="edit-driver-id-number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_driver_contact">DRIVER CONTACT NUMBER</Label>
                        <Input
                          id="edit_driver_contact"
                          value={editData.driver_contact_number}
                          onChange={(e) => setEditData({ ...editData, driver_contact_number: e.target.value })}
                          data-testid="edit-driver-contact"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_driver_license">DRIVERS LICENSE</Label>
                        <Input
                          id="edit_driver_license"
                          value={editData.driver_license}
                          onChange={(e) => setEditData({ ...editData, driver_license: e.target.value })}
                          data-testid="edit-driver-license"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_license_disc">LICENSE DISC</Label>
                        <Input
                          id="edit_license_disc"
                          value={editData.license_disc}
                          onChange={(e) => setEditData({ ...editData, license_disc: e.target.value })}
                          data-testid="edit-license-disc"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_pdp">PDP</Label>
                        <Input
                          id="edit_pdp"
                          value={editData.pdp}
                          onChange={(e) => setEditData({ ...editData, pdp: e.target.value })}
                          placeholder="Professional Driving Permit"
                          data-testid="edit-pdp"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="edit_criminal_checks">CRIMINAL CHECKS</Label>
                        <Input
                          id="edit_criminal_checks"
                          value={editData.criminal_checks}
                          onChange={(e) => setEditData({ ...editData, criminal_checks: e.target.value })}
                          placeholder="Status of criminal background check"
                          data-testid="edit-criminal-checks"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Vehicle Tab */}
                  <TabsContent value="vehicle" className="space-y-4 mt-0">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_vehicle_registration">REGISTRATION NUMBER</Label>
                        <Input
                          id="edit_vehicle_registration"
                          value={editData.vehicle_registration}
                          onChange={(e) => setEditData({ ...editData, vehicle_registration: e.target.value })}
                          placeholder="e.g., CA 123-456"
                          data-testid="edit-vehicle-reg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_vehicle_type">VEHICLE TYPE</Label>
                        <Input
                          id="edit_vehicle_type"
                          value={editData.vehicle_type}
                          onChange={(e) => setEditData({ ...editData, vehicle_type: e.target.value })}
                          placeholder="e.g., Truck, Van, Bakkie"
                          data-testid="edit-vehicle-type"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="edit_vehicle_comments">VEHICLE COMMENTS</Label>
                        <Textarea
                          id="edit_vehicle_comments"
                          value={editData.vehicle_comments}
                          onChange={(e) => setEditData({ ...editData, vehicle_comments: e.target.value })}
                          placeholder="Any notes about the vehicle..."
                          rows={3}
                          data-testid="edit-vehicle-comments"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="edit_allocated_site">ALLOCATED SITE</Label>
                        <Select
                          value={editData.allocated_site_id || '__none__'}
                          onValueChange={(v) => {
                            if (v === '__none__') {
                              setEditData({ ...editData, allocated_site_id: '', allocated_site_name: '' });
                            } else {
                              const s = sites.find(x => x.id === v);
                              setEditData({ ...editData, allocated_site_id: v, allocated_site_name: s?.name || '' });
                            }
                          }}
                        >
                          <SelectTrigger id="edit_allocated_site" data-testid="edit-allocated-site">
                            <SelectValue placeholder="Pick a site (Intertown, DPD Laser, ...)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground">Manage sites under Admin → Sites.</p>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Comments Tab */}
                  <TabsContent value="comments" className="space-y-4 mt-0">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_finance_comments">FINANCE COMMENTS</Label>
                        <Textarea
                          id="edit_finance_comments"
                          value={editData.finance_comments}
                          onChange={(e) => setEditData({ ...editData, finance_comments: e.target.value })}
                          placeholder="Any finance-related notes..."
                          rows={3}
                          data-testid="edit-finance-comments"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_general_comments">GENERAL COMMENTS</Label>
                        <Textarea
                          id="edit_general_comments"
                          value={editData.general_comments}
                          onChange={(e) => setEditData({ ...editData, general_comments: e.target.value })}
                          placeholder="Any general notes..."
                          rows={3}
                          data-testid="edit-general-comments"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
              
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editLoading} data-testid="save-edit-driver">
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Document Expiry Dialog */}
        <ExpiryDialog
          open={showExpiryDialog}
          onOpenChange={setShowExpiryDialog}
          selectedDriver={selectedDriver}
          expiryData={expiryData}
          setExpiryData={setExpiryData}
          onSubmit={handleUpdateExpiry}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteDriverDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          driverToDelete={driverToDelete}
          deleteLoading={deleteLoading}
          onConfirm={handleDeleteDriver}
          onCancel={() => { setShowDeleteDialog(false); setDriverToDelete(null); }}
        />

        {/* Documents Dialog */}
        <DocsDialog
          open={showDocsDialog}
          onOpenChange={setShowDocsDialog}
          docsDriver={docsDriver}
          driverDocs={driverDocs}
          uploadingDoc={uploadingDoc}
          onUpload={handleDocUpload}
        />
      </div>
    </DashboardLayout>
  );
}

